export default class CarValidator {
    constructor(parent) {
        this.parent = parent;
        this.data = parent.data;
        this.pricing = parent.pricing;
        this.wheels = this.data.wheels.map((w) => w.partNumber);
    }

    validate(tempCar) {
        const data = tempCar.data();
        if (!data) {
            // No grade/engine match
            return { valid: false, type: 'noGradeEngine' };
        }

        const colourTrim = tempCar.colourTrim();
        if (!colourTrim) {
            // No colour/trim match
            return { valid: false, type: 'noColourTrim' };
        }

        const wheelCount = tempCar.accessories.filter((a) => this.wheels.includes(a)).length;
        if (wheelCount === 0) {
            // Car has no wheels
            return { valid: false, type: 'noWheels', silent: true };
        } if (wheelCount > 1) {
            // Car has too many wheels
            return { valid: false, type: 'tooManyWheels' };
        }
        const wheel = tempCar.accessories.find((a) => this.wheels.includes(a));
        const carWheel = data.wheels.includes(wheel);
        if (!carWheel) {
            return { valid: false, type: 'noCarWheel' };
        }

        const carAccessories = tempCar.accessories.every((a) => data.accessories.includes(a));
        if (!carAccessories) {
            // Car doesnt do accessory
            return { valid: false, type: 'noCarAccessory' };
        }
        const notCompatible = tempCar.accessories
            .map((a) => this.data.accessories[a])
            .filter((acc) => acc)
            .flatMap((acc) => (acc.notCompatibleWith));
        const accessoryConflict = tempCar.accessories.some((a) => notCompatible.includes(a));
        if (accessoryConflict) {
            // Accessory is not compatible with another;
            return { valid: false, type: 'accessoryConflict' };
        }

        const colourAccessories = tempCar.accessories
            .map((a) => this.data.accessories[a])
            .filter((a) => a && a.extColour);
        if (colourAccessories.length) {
            const wrongColour = colourAccessories.some((a) => a.extColour != tempCar.colour && a.colours[tempCar.colour]);
            if (wrongColour) {
                // Accessory has incorrect colour, but has equivalent in current colour
                return { valid: false, type: 'accessoryColour' };
            }

            const noAccessoryColour = colourAccessories.some((a) => a.extColour != tempCar.colour && !a.colours[tempCar.colour]);
            if (noAccessoryColour) {
                // Accessory has incorrect colour and doesnt have a variant in current colour
                return { valid: false, type: 'noAccessoryColour' };
            }
        }

        // If pricing data is loaded
        const mtoPricing = this.pricing.getCarMTOPricing(tempCar.mto());

        if(mtoPricing) {
            const euroOptions = mtoPricing?.c_euroOptions ?? [];
            const includedEuroOption = euroOptions.find((eo) => eo.c_isMandatory)?.ean;
            const incorrectMandatoryEuroOption = tempCar.mandatoryEuroOption !== includedEuroOption;
            if(incorrectMandatoryEuroOption) {
                // Missing included euro option
                return { valid: false, type: 'incorrectMandatoryEuroOption', silent: true };
            }

            const hasEuroOptions = !!tempCar.e();
            if(hasEuroOptions) {
                const eopBundle = `EOP-BUNDLE|${tempCar.mtoe()}`;
                const euroOptionsBundles = mtoPricing?.c_euroOption_bundles ?? [];
                const euroOptionBundle = euroOptionsBundles.find((euroOption) => eopBundle === euroOption.id);
                if(!euroOptionBundle ) {
                    // No euro option bundle found in pricing data for current accessory list
                    return { valid: false, type: 'noEuroOptionBundle' };
                }
            }

            const additionalProducts = this.pricing.getAdditionalProductsMTO(tempCar.mto()) ?? [];
            const includedAddionalProduct = additionalProducts.filter((ap) => ap.additionalProductIncluded);
            const missingIncludedAdditionalProduct = includedAddionalProduct.some((ap) => !tempCar.additionalProducts.includes(ap.ean));
            if(missingIncludedAdditionalProduct) {
                // Missing additional products that should be included
                return { valid: false, type: 'missingIncludedAdditionalProduct', silent: true };
            }

            const notIncludedAndDiscountedToZero = (ap) => !ap?.additionalProductIncluded && ap?.c_priceAfterDiscount === 0;
            const includedOrDiscountedToZero = (ap) => ap?.additionalProductIncluded || ap?.c_priceAfterDiscount === 0;
            const mustHavesIncludedOrDiscountedToZero = (ap) => !ap.mustHave || ap.mustHave
                ?.map((ap) => this.pricing.getAdditionalProductMTO(ap, tempCar.mto()))
                ?.every((ap) => includedOrDiscountedToZero(ap));
            //Get additional products that are not already included, discounted to zero and all its must haves are also either included or discounted to zero
            const discountedToZeroAdditionalProducts = additionalProducts.filter((ap) => notIncludedAndDiscountedToZero(ap) && mustHavesIncludedOrDiscountedToZero(ap));
            const missingDiscountedToZeroAdditionalProduct = discountedToZeroAdditionalProducts.some((ap) => !tempCar.additionalProducts.includes(ap.ean));
            if(missingDiscountedToZeroAdditionalProduct) {
                // Missing additional products that should be included because it has been discounted to zero
                return { valid: false, type: 'missingDiscountedToZeroAdditionalProduct', silent: true };
            }

            const hasAdditionalProducts = tempCar.additionalProducts.length > 0;
            if(hasAdditionalProducts) {
                const carAdditionalProducts = this.pricing.getCarAdditionalProducts({ tempCar });
                const hasIncompatible = carAdditionalProducts.some((ap) => ap.cantHave?.some((cantHave) => tempCar.additionalProducts.includes(cantHave)));
                if(hasIncompatible) {
                    return { valid: false, type: 'hasIncompatibleAdditionalProduct', silent: true };
                }

                const missingMustHave = carAdditionalProducts.some((ap) => ap.mustHave?.some((mustHave) => !tempCar.additionalProducts.includes(mustHave)));
                if(missingMustHave) {
                    return { valid: false, type: 'missingMustHaveAdditionalProduct', silent: true };
                }
            }
        }

        return { valid: true };
    }
}
